import { useMemo } from "react";
import { useQuery, type ApolloError } from "@apollo/client";
import AiCard from "@/components/AiCard";
import useDebouncedValue from "@/hooks/useDebouncedValue";
import { toDateTimeFormat } from "@/lib/dateHelpers";
import { sendToRaygun } from "@/lib/raygunUtils";
import { toMarkdownBulletList } from "../../_utils";
import { CALL_TOPIC_HIGHLIGHTS_QUERY } from "../queries";

const HIGHLIGHTS_DEBOUNCE_MS = 600;
const HIGHLIGHTS_UNAVAILABLE = "Highlights unavailable right now.";

type Props = {
  startDateTime: string;
  endDateTime: string;
  direction: string | null;
  answered: boolean | null;
  topics: string[];
  workerName: string | null;
  enabled: boolean;
};

export default function TopicHighlights({
  startDateTime,
  endDateTime,
  direction,
  answered,
  topics,
  workerName,
  enabled
}: Props) {
  const filters = useMemo(
    () => ({ startDateTime, endDateTime, direction, answered, topics: [...topics].sort(), workerName }),
    [startDateTime, endDateTime, direction, answered, topics, workerName]
  );
  const debouncedFilters = useDebouncedValue(filters, HIGHLIGHTS_DEBOUNCE_MS);

  const { data, previousData, loading } = useQuery(CALL_TOPIC_HIGHLIGHTS_QUERY, {
    variables: {
      startDateTime: debouncedFilters.startDateTime,
      endDateTime: debouncedFilters.endDateTime,
      direction: debouncedFilters.direction,
      answered: debouncedFilters.answered,
      topics: debouncedFilters.topics,
      workerName: debouncedFilters.workerName
    },
    skip: !enabled || debouncedFilters.topics.length === 0,
    notifyOnNetworkStatusChange: true,
    onError: (queryError: ApolloError) => {
      sendToRaygun(queryError, { tags: ["call-topics", "highlights"] });
    }
  });

  // Drop stale briefs when skipped (e.g. every topic cleared); keep previous
  // data only while a refetch for a non-empty filter is in flight.
  const skipped = !enabled || debouncedFilters.topics.length === 0;
  const highlights = skipped ? undefined : (data ?? previousData)?.callTopicHighlights;
  const busy = loading && !highlights;
  const bullets = highlights?.bullets ?? [];

  return (
    <div data-testid="call-topic-highlights">
      <AiCard
        title="Highlights"
        text={bullets.length > 0 ? toMarkdownBulletList(bullets) : HIGHLIGHTS_UNAVAILABLE}
        loading={busy}
        renderAsMarkdown
      />
      {highlights?.generatedAt && (
        <p
          className="text-body-medium text-sys-brand-on-surface-variant mt-4"
          data-testid="call-topic-highlights-freshness"
        >
          Updated {toDateTimeFormat(highlights.generatedAt)}
        </p>
      )}
    </div>
  );
}
